import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Theme, ThemeSlugs } from './theme';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ThemeLoaderService {
  private readonly document = inject(DOCUMENT);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly http = inject(HttpClient);

  constructor() {
    this.renderer2 = this.rendererFactory.createRenderer(null, null);
  }

  private style: HTMLStyleElement | undefined;
  private cssFile: string = '';
  private readonly themeCSSID: string = 'theme';
  private readonly renderer2: Renderer2;
  private readonly themeLocation = 'assets/css/theme.css';

  setTheme(theme: Theme) {
    this.publishThemeCssVariables(theme);
    this.http
      .get(this.themeLocation, { responseType: 'text' })
      .subscribe((cssThemeTemplate) => {
        this.cssFile = `theme.css`;
        this.removeExistingThemeStyle(this.renderer2, this.themeCSSID);

        // Create a link element via Angular's renderer to avoid SSR troubles
        this.style = this.renderer2.createElement('style') as HTMLStyleElement;
        const styleToApply = this.renderer2.createText(
          this.generateStyles(theme, cssThemeTemplate)
        );
        this.style.appendChild(styleToApply);

        // Set type of the link item and path to the css file
        this.renderer2.setProperty(this.style, 'id', this.themeCSSID);

        // Add the style to the head section
        this.renderer2.appendChild(this.document.head, this.style);
      });
  }

  removeExistingThemeStyle(renderer2: Renderer2, themeCSSID: string) {
    const themeIDHTMlElem = this.document.getElementById(themeCSSID);
    if (themeIDHTMlElem) {
      renderer2.removeChild(this.document.head, themeIDHTMlElem);
    }
  }

  generateStyles(theme: Theme, template: string) {
    let styles = template;

    if (theme.primaryColor) {
      styles = template.replace(
        new RegExp(this.escapeRegExp(ThemeSlugs.PRIMARY_COLOR), 'g'),
        `${theme.primaryColor};`
      );
    }
    if (theme.secondaryColor) {
      styles = styles.replace(
        new RegExp(this.escapeRegExp(ThemeSlugs.SECONDARY_COLOR), 'g'),
        `${theme.secondaryColor};`
      );
    }
    if (theme.btnFontSize) {
      styles = styles.replace(
        new RegExp(this.escapeRegExp(ThemeSlugs.BTN_FONT_SIZE), 'g'),
        `${theme.btnFontSize};`
      );

      styles = styles.replace(
        new RegExp(this.escapeRegExp(ThemeSlugs.BTN_FONT_SIZE_X2), 'g'),
        `${theme.btnFontSizeX2};`
      );
    }
    if (theme.titleFontSize) {
      styles = styles.replace(
        new RegExp(this.escapeRegExp(ThemeSlugs.TITLE_FONT_SIZE), 'g'),
        `${theme.titleFontSize};`
      );

      styles = styles.replace(
        new RegExp(this.escapeRegExp(ThemeSlugs.TITLE_FONT_SIZE_X2), 'g'),
        `${theme.titleFontSizeX2};`
      );
    }
    if (theme.infoPanelFontSize) {
      styles = styles.replace(
        new RegExp(this.escapeRegExp(ThemeSlugs.INFO_PANEL_FONT_SIZE), 'g'),
        `${theme.infoPanelFontSize};`
      );
    }
    if (theme.contentFontSize) {
      styles = styles.replace(
        new RegExp(this.escapeRegExp(ThemeSlugs.CONTENT_FONT_SIZE), 'g'),
        `${theme.contentFontSize};`
      );
    }

    return styles;
  }

  // Expose theme colors as CSS custom properties so decoupled consumers
  // (e.g. the OpenReplay Assist stylesheet) can follow live theme changes.
  private publishThemeCssVariables(theme: Theme) {
    const root = this.document.documentElement;
    if (theme.primaryColor) {
      root.style.setProperty('--theme-primary', theme.primaryColor);
    }
    if (theme.secondaryColor) {
      root.style.setProperty('--theme-secondary', theme.secondaryColor);
    }
  }

  private escapeRegExp(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }
}
